import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import { FedexAccountOutDTO } from '@api/ringil-api.v2';
import ButtonTag from '@components/framework/tags/ButtonTag.tsx';
import SvgTagEdit from '@components/ringil3/icons/TagEdit.tsx';
import { MultiSelect } from 'primereact/multiselect';
import { Field } from '@components/framework/formfield/Field.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { fedexAccountLabel } from '@components/RingilComponents/fedex/fedexAccountLabel.ts';

interface ComponentProps {
  value: string[];
  onChange: (fedexAccountIds: string[]) => void;
  readOnly?: boolean;
}

/**
 * FedEx accounts a location may use. Empty = every account of the company;
 * the distribution form offers only the accounts allowed here.
 */
const LocationFedexAccountsTag: React.FC<ComponentProps> = ({ value, onChange, readOnly }) => {
  const { tr } = useTranslationLgs();
  const { listFedexAccounts } = useContext(ApiContext);
  const [accounts, setAccounts] = useState<FedexAccountOutDTO[]>([]);
  const [internalValue, setInternalValue] = useState<string[]>(value);

  useEffect(() => {
    listFedexAccounts(undefined, undefined, data => setAccounts(data));
  }, []);

  useEffect(() => setInternalValue(value), [value.join('|')]);

  const selected = accounts.filter(a => value.includes(a.id));
  const displayValue = selected.length > 0
    ? selected.map(a => a.accountNumber).join(', ')
    : tr('LocationCarriersApi.fedexAccountsAll', 'All company accounts');

  return (
    <ButtonTag
      label={tr('LocalityDetail.carrierFedex', 'FedEx')}
      value={displayValue}
      actionIcon={<SvgTagEdit />}
      popupWidth={'400px'}
      actionPane={
        <Field label={tr('LocationCarriersApi.fedexAccounts', 'FedEx accounts allowed for this location')}>
          <MultiSelect
            value={internalValue}
            options={accounts}
            optionValue={'id'}
            optionLabel={'accountNumber'}
            itemTemplate={(account: FedexAccountOutDTO) => fedexAccountLabel(account)}
            onChange={e => setInternalValue(e.value ?? [])}
            placeholder={tr('LocationCarriersApi.fedexAccountsAll', 'All company accounts')}
            className={'w-full'}
            display={'chip'}
            data-testid={'location-tag-fedex-accounts'}
          />
        </Field>
      }
      onSave={onSuccess => {
        onChange(internalValue);
        onSuccess();
      }}
      readonly={readOnly}
      autoClose
      dataTestId={'location-tag-fedex'}
    />
  );
};

export default LocationFedexAccountsTag;
